import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import _ from 'lodash';
import mustache from 'mustache';
import { DateTime } from 'luxon';
import { formatTimestamp } from '../../../../utils/time';

highchartsMore(Highcharts);
solidGauge(Highcharts);


const CONTAINER_PROPS = {
  style: {
    height: '100%',
    width: '100%'
  }
};

const DataPointsType = PropTypes.arrayOf(PropTypes.number);

const SignalDataType = PropTypes.shape({
  aggregate: PropTypes.string,
  assetDisplayName: PropTypes.string.isRequired,
  assetId: PropTypes.string.isRequired,
  assetPath: PropTypes.string,
  dataPoints: PropTypes.arrayOf(DataPointsType),
  decimalPoint: PropTypes.number,
  signalDisplayName: PropTypes.string.isRequired,
  signalId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  unit: PropTypes.string
});


const transformPlotlines = (plotlines) => {
  const updatedPlotlines = _.map(plotlines, plotline => {
    return {
      value: plotline.value,
      color: plotline.color,
      dashStyle: plotline.lineStyle,
      width: plotline.width,
      label: {
        text: plotline.displayName
      },
      zIndex: 10
    };
  });
  return { yAxis: { plotLines: updatedPlotlines } };
};

const getTime = (datapoint) => _.first(datapoint);
const getValue = (datapoint) => _.last(datapoint);

const DialGraphComponent = ({ signal, options, valueFormatter, reflowTrigger, plotlines }) => {
  const chartComponent = useRef(null);

  // unit and timestamp are used in template
  const chartOptions = useMemo(() => {
    const header = signal.header;
    // const displayName = signal.displayName;
    const unit = signal.unit;
    const datapoint = _.last(signal.dataPoints);
    const t = !_.isEmpty(datapoint) ? getTime(datapoint) : 0;
    const timestamp = formatTimestamp(DateTime.fromMillis(t ?? 0)) || '';
    const title = mustache.render(options?.yAxis?.title?.text || '', { header }) ?? '';
    return _.merge({}, options, {
      yAxis: {
        title: { text: title }
      },
      series: [{
        name: signal.signalDisplayName,
        data: [getValue(datapoint)],
        dataLabels: {
          format: mustache.render(valueFormatter || '', { unit, timestamp })
        }
      }]
    },
    transformPlotlines(plotlines)
    );
  }, [options, plotlines, signal.dataPoints, signal.header, signal.signalDisplayName, signal.unit, valueFormatter]);

  useEffect(() => {
    if (chartComponent.current) {
      const chart = chartComponent.current.chart;
      chart.reflow();
    }
  }, [reflowTrigger]);

  return (
    <HighchartsReact
      ref={chartComponent}
      highcharts={Highcharts}
      options={chartOptions}
      containerProps={CONTAINER_PROPS}
    />
  );
};

DialGraphComponent.propTypes = {
  signal: SignalDataType.isRequired,
  options: PropTypes.object.isRequired,
  valueFormatter: PropTypes.string
};

export default DialGraphComponent;
